import { useEffect, useRef, useState } from 'react';

import { Block, Button, DataTable, DataTableRef, Tabs } from '@uni-space/uni-components';
import { useTranslation } from 'react-i18next';

import { getAllAcceptanceActs } from '../api';
import { AcceptanceActStatus, CreateAcceptanceActHttpResponse } from '../model/manifestAcceptance';
import { useAcceptanceActsSettingsTable } from '../model/useAcceptanceActsSettingsTable';
import { useManifestAcceptanceStore } from '../model/useManifestAcceptanceStore';
import AcceptanceActDetails from './AcceptanceActDetails';
import AcceptanceScanning from './AcceptanceScanning';
import CreateAcceptanceAct from './CreateAcceptanceAct';
import { ensureStarted, getSortingCenterHub } from '@shared/config/signalR/SortingSettingsHub';

/**
 * Раздел «Приёмки»: все приёмки — открытые и закрытые. Открытая открывается в экран сканирования,
 * закрытая — в карточку с принятыми мешками. Манифест выбирается только при создании приёмки.
 */
export default function ManifestAcceptance() {
  const [tab, setTab] = useState<AcceptanceActStatus>(AcceptanceActStatus.Created);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [detailsActId, setDetailsActId] = useState<number | null>(null);
  const tableRef = useRef<DataTableRef>(null);
  const { t, i18n } = useTranslation();
  const { handleChangeCurrentAcceptance, handleChangeBagsData } = useManifestAcceptanceStore();

  const handleRefetch = () => {
    tableRef.current?.refetchData();
  };

  const { columns, filters, closeConfirmModal } = useAcceptanceActsSettingsTable({
    status: tab,
    onRefetch: handleRefetch,
    onOpenDetails: (act) => setDetailsActId(act.id),
  });

  // Приёмки создают и закрывают с разных ТСД — список обновляем по событиям хаба, а не по таймеру.
  useEffect(() => {
    const conn = getSortingCenterHub();
    const onActChanged = () => handleRefetch();

    conn.on('AcceptanceActCreated', onActChanged);
    conn.on('AcceptanceActClosed', onActChanged);

    (async () => {
      await ensureStarted(conn);
    })();

    return () => {
      conn.off('AcceptanceActCreated', onActChanged);
      conn.off('AcceptanceActClosed', onActChanged);
    };
  }, []);

  const handleCloseScanning = () => {
    handleChangeCurrentAcceptance(null);
    handleChangeBagsData([]);
    // Счётчики мешков в таблице изменились за время сканирования.
    handleRefetch();
  };

  // Приёмка создана — сразу открываем сканирование и показываем её в открытых.
  const handleActCreated = (act: CreateAcceptanceActHttpResponse) => {
    if (!act.id) return;
    setTab(AcceptanceActStatus.Created);
    handleChangeCurrentAcceptance({
      acceptanceActId: act.id,
      acceptanceNumber: act.number ?? null,
      manifestNumber: act.manifestNumber ?? null,
    });
    handleChangeBagsData([]);
  };

  return (
    <>
      <Block sx={{ maxWidth: 700 }}>
        <Tabs
          value={tab}
          onChange={(id) => setTab(id as AcceptanceActStatus)}
          items={[
            { id: AcceptanceActStatus.Created, label: t('manifestAcceptance.tabs.open') },
            { id: AcceptanceActStatus.Closed, label: t('manifestAcceptance.tabs.closed') },
          ]}
        />
      </Block>

      <CreateAcceptanceAct
        isOpen={isOpenCreate}
        onClose={() => setIsOpenCreate(false)}
        onCreated={handleActCreated}
        onRefetch={handleRefetch}
      />
      <AcceptanceScanning onClose={handleCloseScanning} onRefetch={handleRefetch} />
      <AcceptanceActDetails acceptanceActId={detailsActId} onClose={() => setDetailsActId(null)} />
      {closeConfirmModal}

      {/* key: вкладки — это разные выборки, таблица должна пересоздаться, а не показать старые строки в новых колонках */}
      <DataTable
        key={tab}
        columns={columns}
        ref={tableRef}
        filters={filters}
        mobileWidth={678}
        renderControls={
          <Button onClick={() => setIsOpenCreate(true)}>{t('manifestAcceptance.actions.createAcceptance')}</Button>
        }
        onFetch={(page, pageSize, tableFilters, ctx) => getAllAcceptanceActs(page, pageSize, tableFilters, tab, ctx)}
        lang={i18n.language}
        height="calc(100vh - 220px)"
      />
    </>
  );
}
